"use strict";
/*!
Copyright (C) 2020 Evolved Binary

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <https://www.gnu.org/licenses/>.
*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.XditaSerializer = void 0;
const lwdita_ast_1 = require("@evolvedbinary/lwdita-ast");
/**
 * Serializer for XDITA.
 * Takes an AST and serializes it to XDITA.
 */
class XditaSerializer {
    /**
     * Constructor
     *
     * @param outputStream - The output stream.
     * @param indent - enable indentation
     * @param indentation - the character (or string) to use as the indent
     * @param tabSize - size of the tab, only used when the `indentation` is not a `\t` character.
     */
    constructor(outputStream, indent = false, indentation = " ", tabSize = 4) {
        this.depth = 0;
        this.outputStream = outputStream;
        this.indent = indent;
        this.tabSize = tabSize;
        this.indentation = indentation;
        if (indentation === '\t') {
            this.tabSize = 1;
        }
        this.EOL = '\n';
    }
    // Temporary fix for the serializer of images and fallback nodes see:
    // https://evolvedbinaryltd.kanbanize.com/ctrl_board/16/cards/3516/details/
    serializeMixedContent(node) {
        //This is a temporary solution REMOVE the alt node as it's not mixed content
        // by YB, signed-off by AR.
        const alt = new lwdita_ast_1.AltNode();
        return node.allowsMixedContent() || node.canAddNode(alt);
    }
    /**
     * Emit the indentation to the output stream
     *
     * @param node - the node to check if indentation can be added
     */
    serializeIndentation(node) {
        if (!this.indent || !node)
            return;
        if (this.serializeMixedContent(node))
            return;
        this.outputStream.emit(this.indentation.repeat(this.depth * this.tabSize));
    }
    /**
     * Emit the End of Line (EOL) character to the output stream
     *
     * @param node - the node to check if EOL can be added
     */
    serializeEOL(node) {
        if (!this.indent || !node)
            return;
        if (this.serializeMixedContent(node))
            return;
        this.outputStream.emit(this.EOL);
    }
    /**
     * Serialize a document node to the output stream.
     *
     * @param node - the document node to serialize
     */
    serializeDocument(node) {
        var _a, _b;
        // emit the XML declaration and doctype declaration
        const xmlDeclaration = `<?xml version="${((_a = node.xmlDecl) === null || _a === void 0 ? void 0 : _a.version) || "1.0"}" encoding="${((_b = node.xmlDecl) === null || _b === void 0 ? void 0 : _b.encoding) || "UTF-8"}"?>`;
        const docTypeDeclaration = `<!DOCTYPE${node.doctype || ' topic PUBLIC "-//OASIS//DTD LIGHTWEIGHT DITA Topic//EN" "lw-topic.dtd"'}>`;
        this.outputStream.emit(xmlDeclaration);
        this.outputStream.emit(this.EOL);
        this.outputStream.emit(docTypeDeclaration);
        this.outputStream.emit(this.EOL);
        // a document node has no string representation, so move on to its children
        node.children.forEach(child => this.serialize(child, node));
    }
    /**
     * Serialize an element node to the output stream.
     *
     * @param node - the element node to serialize
     */
    serializeElement(node) {
        var _a;
        // serialize the start of the element start tag
        this.outputStream.emit(`<${node.static.nodeName}`);
        // serialize the attributes
        this.serializeAttributes(node);
        if ((_a = node.children) === null || _a === void 0 ? void 0 : _a.length) {
            // as the element has children or attributes, serialize the remainder of the element start tag
            this.outputStream.emit(`>`);
            this.serializeEOL(node);
            // increment the depth after starting an element
            this.depth++;
            // visit the element's children
            node.children.forEach(child => this.serialize(child, node));
            // decrement the depth after serializing the elements children
            this.depth--;
            this.serializeIndentation(node);
            this.outputStream.emit(`</${node.static.nodeName}>`);
        }
        else {
            // element has no attributes or children, so the remainder of the element start tag as a self-closing element
            this.outputStream.emit(`/>`);
        }
    }
    /**
     * Serialize the attributes to the output stream
     *
     * @param node - the node to serialize the attributes of
     */
    serializeAttributes(node) {
        let attrsStr = '';
        const props = node.getProps();
        if (props) {
            const attr = props;
            attrsStr = Object.keys(props).filter(key => attr[key]).map(key => `${key}="${attr[key]}"`).join(' ');
        }
        if (attrsStr.length) {
            attrsStr = ` ${attrsStr}`;
        }
        this.outputStream.emit(attrsStr);
    }
    /**
     * Serialize the text content of the text node to the output stream
     *
     * @param node - the text node to serialize the content of
     */
    serializeText(node) {
        const props = node.getProps();
        if (props['content']) {
            this.outputStream.emit(String(props['content']));
        }
    }
    /**
     * Serialize the content of cdata to the output stream
     *
     * @param node - the node to serialize the content of
     */
    serializeCData(node) {
        const props = node.getProps();
        if (props['content']) {
            const cdataOpen = `<![CDATA[`;
            const cdataClose = `]]>`;
            this.outputStream.emit(cdataOpen + String(props['content']) + cdataClose);
        }
    }
    /**
     * Visit a node and serialize it to the output stream
     *
     * @param node - the node to serialize
     * @param parent - the parent node of the node being serialized
     */
    serialize(node, parent) {
        if (node instanceof lwdita_ast_1.DocumentNode) {
            this.serializeDocument(node);
            // close the output stream as we have now serialized the document
            this.outputStream.close();
        }
        else {
            // serialize any indentation
            this.serializeIndentation(parent);
            if (node instanceof lwdita_ast_1.TextNode) {
                // if the node is a text node, serialize its text content
                this.serializeText(node);
            }
            else if (node instanceof lwdita_ast_1.CDataNode) {
                // if the node is a text node, serialize its text content
                this.serializeCData(node);
            }
            else {
                // TODO(AR) ideally we should have `node instanceof ElementNode` type guard here
                this.serializeElement(node);
            }
            // serialize any EOL
            this.serializeEOL(parent);
        }
    }
}
exports.XditaSerializer = XditaSerializer;
//# sourceMappingURL=xdita-serializer.js.map